/**
 * useWorkflowTest (P4.T8)
 *
 * RQ-based hook coordinating three mutations against the workflow test
 * endpoints (backend commits 4 + 5):
 *
 *   start      → POST /api/workflows/:workflowId/test
 *   fireEvent  → POST /api/workflows/test-instances/:instanceId/fire-event
 *   abort      → POST /api/workflows/instances/:instanceId/abort  (204)
 *
 * Derives a 5-state state machine from mutation flight + paused field +
 * finalStatus:
 *
 *   idle | running | paused | terminal | aborted
 *
 * Spec ref: §3.7 + §4.1.
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { apiClient } from '@/services/apiClient'

// ── Types ──────────────────────────────────────────────────────────────────

export type WorkflowStatus =
  | 'active'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'abandoned'
  | 'scheduled'

export type WorkflowTestEvent = {
  id: string
  event_type: string
  node_id?: string | null
  payload: Record<string, unknown>
  created_at: string
}

export type WorkflowTestResponse = {
  instance_id: string
  conversation_id: string
  events: WorkflowTestEvent[]
  final_status: WorkflowStatus
  slots: Record<string, unknown>
  paused: boolean
  paused_for_event: string | null
  paused_correlation: string | null
  passed: boolean | null
  trace_truncated: boolean
}

export type StartArgs = {
  slots: Record<string, unknown>
  triggers: Record<string, unknown>
  expected_final_status?: WorkflowStatus | null
}

export type FireEventArgs = {
  event_name: string
  correlation: string
  payload: Record<string, unknown>
}

export type UseWorkflowTestArgs = {
  bundleId: string
  workflowId: string
}

export type WorkflowTestState =
  | 'idle'
  | 'running'
  | 'paused'
  | 'terminal'
  | 'aborted'

export type UseWorkflowTestResult = {
  start: UseMutationResult<WorkflowTestResponse, Error, StartArgs>
  fireEvent: UseMutationResult<WorkflowTestResponse, Error, FireEventArgs>
  abort: UseMutationResult<void, Error, void>
  instanceId: string | null
  state: WorkflowTestState
  events: WorkflowTestEvent[]
  finalStatus: WorkflowStatus | null
  slots: Record<string, unknown>
  paused: { event_name: string; correlation: string } | null
  passed: boolean | null
  traceTruncated: boolean
  reset: () => void
}

// ── Constants ──────────────────────────────────────────────────────────────

const TERMINAL_STATUSES: WorkflowStatus[] = ['completed', 'failed', 'abandoned']

// ── Hook ───────────────────────────────────────────────────────────────────

export function useWorkflowTest({ bundleId, workflowId }: UseWorkflowTestArgs): UseWorkflowTestResult {
  const [instanceId, setInstanceId] = useState<string | null>(null)
  const [events, setEvents] = useState<WorkflowTestEvent[]>([])
  const [finalStatus, setFinalStatus] = useState<WorkflowStatus | null>(null)
  const [slots, setSlots] = useState<Record<string, unknown>>({})
  const [paused, setPaused] = useState<{ event_name: string; correlation: string } | null>(null)
  const [passed, setPassed] = useState<boolean | null>(null)
  const [traceTruncated, setTraceTruncated] = useState(false)
  const [aborted, setAborted] = useState(false)

  const applyResponse = useCallback((data: WorkflowTestResponse) => {
    setInstanceId(data.instance_id)
    setEvents(data.events)
    setFinalStatus(data.final_status)
    setSlots(data.slots)
    setPassed(data.passed)
    setTraceTruncated(data.trace_truncated)
    setPaused(
      data.paused && data.paused_for_event && data.paused_correlation
        ? { event_name: data.paused_for_event, correlation: data.paused_correlation }
        : null,
    )
    setAborted(false)
  }, [])

  const reset = useCallback(() => {
    setInstanceId(null)
    setEvents([])
    setFinalStatus(null)
    setSlots({})
    setPaused(null)
    setPassed(null)
    setTraceTruncated(false)
    setAborted(false)
  }, [])

  const start = useMutation<WorkflowTestResponse, Error, StartArgs>({
    mutationFn: async (args: StartArgs): Promise<WorkflowTestResponse> => {
      return await apiClient.post<WorkflowTestResponse>(
        `/api/workflows/${workflowId}/test`,
        { bundle_id: bundleId, ...args },
      )
    },
    onSuccess: applyResponse,
  })

  const fireEvent = useMutation<WorkflowTestResponse, Error, FireEventArgs>({
    mutationFn: async (args: FireEventArgs): Promise<WorkflowTestResponse> => {
      if (!instanceId) throw new Error('No active test instance')
      return await apiClient.post<WorkflowTestResponse>(
        `/api/workflows/test-instances/${instanceId}/fire-event`,
        args,
      )
    },
    onSuccess: applyResponse,
  })

  const abort = useMutation<void, Error, void>({
    mutationFn: async (): Promise<void> => {
      if (!instanceId) throw new Error('No active test instance')
      const response = await apiClient.fetch(
        `/api/workflows/instances/${instanceId}/abort`,
        { method: 'POST' },
      )
      if (!response.ok) {
        const body = await response.text().catch(() => '')
        throw new Error(`Abort failed (${response.status}): ${body}`)
      }
    },
    onSuccess: () => {
      setAborted(true)
      setPaused(null)
    },
  })

  const state: WorkflowTestState = useMemo(() => {
    if (aborted) return 'aborted'
    if (start.isPending || fireEvent.isPending) return 'running'
    if (paused) return 'paused'
    if (finalStatus && TERMINAL_STATUSES.includes(finalStatus)) return 'terminal'
    return 'idle'
  }, [aborted, start.isPending, fireEvent.isPending, paused, finalStatus])

  return {
    start,
    fireEvent,
    abort,
    instanceId,
    state,
    events,
    finalStatus,
    slots,
    paused,
    passed,
    traceTruncated,
    reset,
  }
}
